import React from 'react';
import { useParams } from 'react-router-dom';

import { GameContext } from '../contexts/game';
import { PlayerContext } from '../contexts/player';
import { getFromStore, putToStore } from '../utils/indexedDB';
import {
  activeUploads,
  base64ToBlob,
  getUploadKey,
  TFileUpload,
  useUploadChunk
} from './use-upload-chunk';

export const useUploadOnBackground = () => {
  const { gameCode } = useParams();
  const [player] = React.useContext(PlayerContext);
  const [, , , isOnline] = React.useContext(GameContext);

  const { startUpload } = useUploadChunk({ skipExecuteInTask: true });

  const playerCode = player.playerState?.code || '';
  const openedTaskId = (player.playerState?.openedTask || []).at(0);
  const openedOnDemandTaskId = player.playerState?.openedOnDemandTask;

  React.useEffect(() => {
    if (!playerCode) return;

    (async () => {
      const uploads = await getFromStore<TFileUpload>(
        'upload-queue',
        playerCode
      );

      if (!uploads) await putToStore('upload-queue', { playerCode, files: [] });
    })();
  }, [playerCode]);

  React.useEffect(() => {
    if (!gameCode || !isOnline || !playerCode) return;

    (async () => {
      const uploads = await getFromStore<TFileUpload>(
        'upload-queue',
        playerCode
      );
      if (!uploads || uploads.files.length === 0) return;

      const pendingFiles = uploads.files
        .filter((f) => f.status === 'pending' && f.base64Data)
        .filter((f) => f.taskId !== openedTaskId)
        .filter((f) => f.taskId !== openedOnDemandTaskId);

      for (const file of pendingFiles) {
        const key = getUploadKey(playerCode, file.taskId, file.formId);
        // Do not conflict if already uploading in task
        if (activeUploads.has(key)) continue;

        try {
          const blob = base64ToBlob(file.base64Data!);
          const fileObj = new File([blob], file.fileName ?? '', {
            type: file.fileType,
            lastModified: file.lastModified
          });

          await startUpload(
            fileObj,
            file.taskId,
            file.formId,
            file.isManualScoring,
            file.lastUploadedChunk || 0
          );
        } catch (err) {
          console.error(
            '[useUploadOnBackground] Failed to process background upload:',
            err
          );
        }
      }
    })();
  }, [gameCode, isOnline, playerCode, openedTaskId, openedOnDemandTaskId]);
};
